import { invokeFunction, supabase } from '@/integrations/supabase/client';
import type { ReportDataSources, ReportSourceChoice, ReportSourceKey } from '@/shared/types/reportDataSources';
import { readFunctionErrorBody } from '@/utils/functionErrorBody';

// Saves one Settings › Reports and Charts › Data sources row (FIG-345 · UC-229)
// through the admin-only edge function, which also records the change.
// v2 (FIG-409 · UC-233) also accepts several sources: { kind: 'sources', ids }.

export interface SaveReportDataSourceResult {
  ok: boolean;
  reportDataSources?: ReportDataSources;
  /** False when the setting was saved but its history entry wasn't. */
  recorded?: boolean;
  error?: string;
}

interface FunctionResponse {
  success?: boolean;
  reportDataSources?: ReportDataSources;
  recorded?: boolean;
  error?: string;
}

export async function saveReportDataSource(
  organisationId: string,
  key: ReportSourceKey,
  choice: ReportSourceChoice,
): Promise<SaveReportDataSourceResult> {
  try {
    const { data, error } = await invokeFunction<FunctionResponse>(supabase, 'update-merchant-report-sources-v2', {
      body: { organisationId, key, choice },
    });
    if (error || !data?.success) {
      return { ok: false, error: data?.error ?? (error instanceof Error ? error.message : 'Could not save the data source') };
    }
    return { ok: true, reportDataSources: data.reportDataSources, recorded: data.recorded !== false };
  } catch (error) {
    // A non-2xx response is thrown; its JSON body (e.g. "That data source is not
    // connected to this merchant") is only on the error's context (FIG-345 sub-PR 3 fix).
    const body = await readFunctionErrorBody<FunctionResponse>(error);
    return { ok: false, error: body?.error ?? (error instanceof Error ? error.message : 'Could not save the data source') };
  }
}
